import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, ExternalLink } from "lucide-react";

const footerLinks = {
  solutions: [
    { label: "Defence & Counter-UAS", href: "/solutions/defence-cuas" },
    { label: "Protective Security", href: "/solutions/protective-security" },
    { label: "Public Safety", href: "/solutions/public-safety" },
    { label: "Civil Intelligence", href: "/solutions/civil-intelligence" },
  ],
  platforms: [
    { label: "UAVSimNet", href: "https://c-uas.co.uk", external: true },
    { label: "AI-AV", href: "/platforms/ai-av" },
    { label: "Helais", href: "/platforms/helais" },
    { label: "UrbanAnalytix", href: "https://urban-analytix.co.uk", external: true },
  ],
  company: [
    { label: "About RLK", href: "/company" },
    { label: "Our Companies", href: "/company/structure" },
    { label: "Intellectual Property", href: "/company/intellectual-property" },
    { label: "Evidence & Assurance", href: "/evidence" },
    { label: "Insights", href: "/insights" },
    { label: "Contact", href: "/contact" },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-card/30">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Main Footer */}
        <div className="py-16 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-12">
            {/* Brand */}
            <div className="lg:col-span-4">
              <Link to="/" className="inline-block mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png"
                  alt="The RLK Group"
                  className="h-10 w-auto"
                />
              </Link>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mb-8">
                Operational intelligence, mission software and autonomy technologies for defence, public safety and critical infrastructure.
              </p>
              <div className="space-y-3">
                <a
                  href="mailto:info@rlktech.co.uk"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  info@rlktech.co.uk
                </a>
                <a
                  href="tel:01413194813"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  0141 319 4813
                </a>
                <div className="flex items-start gap-3 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Glasgow, Scotland</span>
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 lg:gap-12">
                <div>
                  <h4 className="text-caption mb-4">Solutions</h4>
                  <ul className="space-y-3">
                    {footerLinks.solutions.map((link) => (
                      <li key={link.href}>
                        <Link
                          to={link.href}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-caption mb-4">Platforms</h4>
                  <ul className="space-y-3">
                    {footerLinks.platforms.map((link) => (
                      <li key={link.href}>
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                          >
                            {link.label}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {link.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-caption mb-4">Company</h4>
                  <ul className="space-y-3">
                    {footerLinks.company.map((link) => (
                      <li key={link.href}>
                        <Link
                          to={link.href}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-border/50">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground">
              © {currentYear} The RLK Group Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <p className="text-xs text-muted-foreground hidden sm:block">
                RLK, UrbanAnalytix, and AI-AV are registered trademarks.
              </p>
              <a
                href="https://www.linkedin.com/company/the-rlk-group/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
